import { Loader2, Trash2, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { useAuth } from "@/contexts/AuthContext";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { userKeys } from "@/lib/queryKeys";
import {
  listPlatformUsers,
  listPendingRootInvites,
  inviteRootUser,
  resendRootInvite,
  cancelRootInvite,
  deletePlatformUser,
  type PlatformRole,
  type PlatformUser,
} from "@/services/platformUsersService";

function statusVariant(
  status: string
): "success" | "warning" | "danger" | "info" {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "disabled":
    case "expired":
      return "danger";
    default:
      return "info";
  }
}

export function UsersPage(): JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<PlatformRole>("root_admin");
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  return (
    <PlatformUsersContent
      pagination={pagination}
      inviteOpen={inviteOpen}
      setInviteOpen={setInviteOpen}
      inviteEmail={inviteEmail}
      setInviteEmail={setInviteEmail}
      inviteRole={inviteRole}
      setInviteRole={(r) => setInviteRole(r)}
      cancellingId={cancellingId}
      setCancellingId={setCancellingId}
      resendingId={resendingId}
      setResendingId={setResendingId}
      isRoot={user?.platformRole === "root"}
      currentUserId={user?.id ?? ""}
      queryClient={queryClient}
    />
  );
}

function PlatformUsersContent({
  pagination,
  inviteOpen,
  setInviteOpen,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  cancellingId,
  setCancellingId,
  resendingId,
  setResendingId,
  isRoot,
  currentUserId,
  queryClient,
}: {
  pagination: ReturnType<typeof usePaginationFromUrl>;
  inviteOpen: boolean;
  setInviteOpen: (v: boolean) => void;
  inviteEmail: string;
  setInviteEmail: (v: string) => void;
  inviteRole: PlatformRole;
  setInviteRole: (v: PlatformRole) => void;
  cancellingId: string | null;
  setCancellingId: (v: string | null) => void;
  resendingId: string | null;
  setResendingId: (v: string | null) => void;
  isRoot: boolean;
  currentUserId: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  const { t } = useTranslation();
  const [removeTarget, setRemoveTarget] = useState<PlatformUser | null>(null);

  const {
    data: usersData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: userKeys.list({ page: pagination.page, limit: pagination.limit }),
    queryFn: () =>
      listPlatformUsers({ page: pagination.page, limit: pagination.limit }),
  });

  const { data: pendingInvites = [] } = useQuery({
    queryKey: userKeys.pendingInvites(),
    queryFn: listPendingRootInvites,
  });

  useEffect(() => {
    if (usersData != null) {
      pagination.setTotal(usersData.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [usersData, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("users.page.error"));
    }
  }, [isError, t]);

  const inviteMutation = useMutation({
    mutationFn: (payload: { email: string; platformRole: PlatformRole }) =>
      inviteRootUser(payload),
    onSuccess: () => {
      toast.success(t("users.invite.success"));
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("root_admin");
      void queryClient.invalidateQueries({ queryKey: userKeys.pendingInvites() });
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    onError: () => {
      toast.error(t("users.invite.error"));
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: cancelRootInvite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.pendingInvites() });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: resendRootInvite,
    onSuccess: () => {
      toast.success(t("users.page.resendSuccess"));
      void queryClient.invalidateQueries({ queryKey: userKeys.pendingInvites() });
    },
    onError: () => {
      toast.error(t("users.invite.error"));
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: deletePlatformUser,
    onSuccess: () => {
      toast.success(t("users.page.removeSuccess"));
      setRemoveTarget(null);
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
    onError: () => {
      toast.error(t("users.page.removeError"));
    },
  });

  const handleInviteSubmit = (): void => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    inviteMutation.mutate({ email, platformRole: inviteRole });
  };

  const handleCancelInvite = (id: string): void => {
    setCancellingId(id);
    cancelInviteMutation.mutate(id, {
      onSettled: () => setCancellingId(null),
    });
  };

  const handleResendInvite = (id: string): void => {
    setResendingId(id);
    resendInviteMutation.mutate(id, {
      onSettled: () => setResendingId(null),
    });
  };

  const users = usersData?.data ?? [];
  const loading = isLoading;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("users.page.title")}
        description={t("users.page.description")}
        actions={
          isRoot ? (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              {t("users.page.addUser")}
            </Button>
          ) : undefined
        }
      />
      <TableCard className="mt-4">
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : users.length === 0 ? (
          <EmptyState title={t("users.page.empty")} />
        ) : (
          <>
            <ResponsiveListSplit
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("users.page.columns.name")}</TableHead>
                      <TableHead>{t("users.page.columns.email")}</TableHead>
                      <TableHead>{t("users.page.columns.role")}</TableHead>
                      <TableHead>{t("users.page.columns.status")}</TableHead>
                      <TableHead>{t("users.page.columns.addedBy")}</TableHead>
                      {isRoot ? (
                        <TableHead className="w-[72px] text-right">
                          {t("users.page.columns.actions")}
                        </TableHead>
                      ) : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>{u.displayName?.trim() || u.email}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{t(`users.page.role.${u.platformRole}`)}</TableCell>
                        <TableCell>
                          <StatusBadge variant={statusVariant(u.status)}>
                            {t(`users.page.status.${u.status}`)}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>—</TableCell>
                        {isRoot ? (
                          <TableCell className="text-right">
                            {u.id !== currentUserId ? (
                              <TableActionsMenu
                                actions={[
                                  {
                                    label: t("users.page.removeUser"),
                                    icon: Trash2,
                                    variant: "destructive",
                                    onClick: () => setRemoveTarget(u),
                                  },
                                ]}
                              />
                            ) : null}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              }
              cards={
                <div className="divide-y divide-border">
                  {users.map((u) => (
                    <DataListCardRow key={u.id}>
                      <p className="font-semibold leading-snug text-foreground">
                        {u.displayName?.trim() || u.email}
                      </p>
                      <div className="mt-3 space-y-3">
                        <DataListCardField label={t("users.page.columns.email")}>
                          {u.email}
                        </DataListCardField>
                        <DataListCardField label={t("users.page.columns.role")}>
                          {t(`users.page.role.${u.platformRole}`)}
                        </DataListCardField>
                        <DataListCardField label={t("users.page.columns.status")}>
                          <StatusBadge variant={statusVariant(u.status)}>
                            {t(`users.page.status.${u.status}`)}
                          </StatusBadge>
                        </DataListCardField>
                        <DataListCardField label={t("users.page.columns.addedBy")}>—</DataListCardField>
                        {isRoot && u.id !== currentUserId ? (
                          <div className="pt-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-destructive/50 text-destructive transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setRemoveTarget(u)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t("users.page.removeUser")}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </DataListCardRow>
                  ))}
                </div>
              }
            />
            {pagination.total > 0 && (
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t("users.page.rowsPerPage")}
                pageOfLabel={t("users.page.pageOf", {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  ),
                  total: pagination.total,
                })}
                onRefresh={() => void refetch()}
                isRefreshing={isFetching}
              />
            )}
          </>
        )}
      </TableCard>

      {pendingInvites.length > 0 && (
        <div className="mt-6">
          <h2 className="mb-2 text-lg font-medium text-foreground">
            {t("users.page.pendingInvites")}
          </h2>
          <TableCard>
            <ResponsiveListSplit
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("users.page.columns.email")}</TableHead>
                      <TableHead>{t("users.page.columns.role")}</TableHead>
                      <TableHead>{t("users.page.columns.status")}</TableHead>
                      <TableHead>{t("users.page.invitedBy")}</TableHead>
                      <TableHead className="w-[180px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingInvites.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell>{t(`users.page.role.${inv.platformRole}`)}</TableCell>
                        <TableCell>
                          <StatusBadge
                            variant={inv.expired ? "danger" : statusVariant("pending")}
                          >
                            {inv.expired
                              ? t("users.page.status.expired")
                              : t("users.page.status.pending")}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>{inv.invitedByEmail ?? "—"}</TableCell>
                        <TableCell>
                          {isRoot ? (
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void handleResendInvite(inv.id)}
                                disabled={
                                  resendingId === inv.id || cancellingId === inv.id
                                }
                              >
                                {resendingId === inv.id ? (
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : null}
                                {t("users.page.resendInvite")}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void handleCancelInvite(inv.id)}
                                disabled={
                                  cancellingId === inv.id || resendingId === inv.id
                                }
                              >
                                {t("users.page.cancelInvite")}
                              </Button>
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              }
              cards={
                <div className="divide-y divide-border">
                  {pendingInvites.map((inv) => (
                    <DataListCardRow key={inv.id}>
                      <p className="font-semibold leading-snug text-foreground">{inv.email}</p>
                      <div className="mt-3 space-y-3">
                        <DataListCardField label={t("users.page.columns.role")}>
                          {t(`users.page.role.${inv.platformRole}`)}
                        </DataListCardField>
                        <DataListCardField label={t("users.page.columns.status")}>
                          <StatusBadge
                            variant={inv.expired ? "danger" : statusVariant("pending")}
                          >
                            {inv.expired
                              ? t("users.page.status.expired")
                              : t("users.page.status.pending")}
                          </StatusBadge>
                        </DataListCardField>
                        <DataListCardField label={t("users.page.invitedBy")}>
                          {inv.invitedByEmail ?? "—"}
                        </DataListCardField>
                        {isRoot ? (
                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleResendInvite(inv.id)}
                              disabled={
                                resendingId === inv.id || cancellingId === inv.id
                              }
                              className="transition-colors duration-150"
                            >
                              {resendingId === inv.id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : null}
                              {t("users.page.resendInvite")}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleCancelInvite(inv.id)}
                              disabled={
                                cancellingId === inv.id || resendingId === inv.id
                              }
                              className="transition-colors duration-150"
                            >
                              {t("users.page.cancelInvite")}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </DataListCardRow>
                  ))}
                </div>
              }
            />
          </TableCard>
        </div>
      )}

      <ConfirmationDialog
        open={removeTarget != null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title={t("users.page.removeConfirmTitle")}
        description={
          removeTarget
            ? t("users.page.removeConfirmDescription", { email: removeTarget.email })
            : ""
        }
        confirmText={t("users.page.removeUser")}
        cancelText={t("users.invite.cancel")}
        variant="destructive"
        isLoading={removeUserMutation.isPending}
        loadingText={t("users.page.removeUser")}
        onConfirm={async () => {
          if (!removeTarget) return;
          await removeUserMutation.mutateAsync(removeTarget.id);
        }}
      />

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("users.invite.title")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="invite-email">
                {t("users.invite.email")}
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="invite-role">
                {t("users.invite.role")}
              </Label>
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as PlatformRole)}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="root">
                    {t("users.page.role.root")}
                  </SelectItem>
                  <SelectItem value="root_admin">
                    {t("users.page.role.root_admin")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInviteOpen(false)}
              disabled={inviteMutation.isPending}
            >
              {t("users.invite.cancel")}
            </Button>
            <Button
              onClick={() => void handleInviteSubmit()}
              disabled={!inviteEmail.trim() || inviteMutation.isPending}
            >
              {inviteMutation.isPending
                ? t("users.invite.submitting")
                : t("users.invite.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

